import { Box, Card, CardContent, Divider, Link, Stack, TablePagination, Typography } from "@mui/material";
import { createColumnHelper } from "@tanstack/react-table";
import { useState } from "react";
import { Table, TableSkeleton } from "src/components";
import { GetPageMetricsPageDto, useGetPageMetrics } from "src/http";

type DateParams = {
  startDateUtc: string;
  endDateUtc: string;
};

interface IProps {
  dateParams: DateParams;
}

const columnHelper = createColumnHelper<GetPageMetricsPageDto>();

const columns = [
  columnHelper.accessor((row) => (row.path === "/settings" ? "User Settings" : row.pageTitle), {
    id: "pageTitle",
    header: "Page",
    size: 200,
    minSize: 150,
    meta: {
      hideColumnMenu: true,
      flex: true,
    },
    enableResizing: false,
  }),
  columnHelper.accessor("path", {
    header: "Path",
    size: 300,
    minSize: 150,
    cell: (info) => (
      <Link href={info.getValue()} target="_blank" rel="noopener noreferrer">
        {info.getValue()}
      </Link>
    ),
    meta: {
      hideColumnMenu: true,
      flex: true,
    },
    enableResizing: false,
  }),
  columnHelper.accessor("pageViews", {
    header: "Page Views",
    size: 150,
    minSize: 100,
    meta: {
      hideColumnMenu: true,
    },
    enableResizing: false,
  }),
  columnHelper.accessor("uniqueVisitors", {
    header: "Unique Visitors",
    size: 150,
    minSize: 120,
    meta: {
      hideColumnMenu: true,
    },
    enableResizing: false,
  }),
];

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const PageMetricsTable = (props: IProps) => {
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);

  const { data, isLoading, isFetching } = useGetPageMetrics({
    ...props.dateParams,
    page: pageIndex,
    pageSize,
  });

  return (
    <Card>
      {isLoading || isFetching || !data ? (
        <CardContent>
          <TableSkeleton />
        </CardContent>
      ) : (
        <Stack divider={<Divider />}>
          <Table
            tableId="page-metrics-table"
            data={data.pages}
            columns={columns}
            enableSorting={false}
            placeholder={
              <Box sx={{ p: 1 }}>
                <Typography variant="placeholder">Nothing here yet.</Typography>
              </Box>
            }
          />
          <TablePagination
            component="div"
            count={data.totalResultCount}
            page={pageIndex}
            onPageChange={(_, newPage) => setPageIndex(newPage)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => {
              setPageSize(Number(e.target.value));
              setPageIndex(0);
            }}
            rowsPerPageOptions={PAGE_SIZE_OPTIONS}
            showFirstButton
            showLastButton
          />
        </Stack>
      )}
    </Card>
  );
};

export { PageMetricsTable };
